import * as com from "../Common"

/** Reserved section: resolve to first incomplete step after V2 application loads. */
export const APPLICATION_V2_CONTINUE_SECTION = "continue"

export const APPLICATION_V2_CONTINUE_PATH = `/application/v2/interview/${APPLICATION_V2_CONTINUE_SECTION}`

/** Wizard sections in sidebar order (matches StepApplicationV2). */
export const APPLICATION_V2_FLOW_SECTIONS = [
    "yourinfo",
    "residence",
    "property",
    "employment",
    "otherincome",
    "assets",
    "realestate",
    "liabilities",
    "propertymoney",
    "finances",
    "creditcheck",
]

const LEGACY_STEP_TO_SECTION = [
    [com.stageToNumberApplication.creditcheck, "creditcheck"],
    [com.stageToNumberApplication.finances, "finances"],
    [com.stageToNumberApplication.propertymoney, "propertymoney"],
    [com.stageToNumberApplication.liabilities, "liabilities"],
    [com.stageToNumberApplication.realestate, "realestate"],
    [com.stageToNumberApplication.assets, "assets"],
    [com.stageToNumberApplication.income, "employment"],
    [com.stageToNumberApplication.property, "property"],
    [com.stageToNumberApplication.residence, "residence"],
    [com.stageToNumberApplication.applicant, "yourinfo"],
]

/**
 * Map old progress.interview.step to a V2 section.
 * Mirrors Continue capping at overview when step is past the interview wizard.
 */
export const mapLegacyInterviewStepToV2Section = (step) => {
    const n = parseInt(step, 10)
    if (Number.isNaN(n) || n < com.stageToNumberApplication.applicant) {
        return "yourinfo"
    }
    if (n >= com.stageToNumberApplication.overview) {
        return "overview"
    }
    for (const [threshold, section] of LEGACY_STEP_TO_SECTION) {
        if (n >= threshold) {
            return section
        }
    }
    return "yourinfo"
}

/**
 * First incomplete interview section; overview when all flow steps are done.
 * Same cap as old Continue (Math.min(overview, step)).
 */
export const firstIncompleteV2Section = (completedSteps = {}) => {
    for (const section of APPLICATION_V2_FLOW_SECTIONS) {
        if (!completedSteps[section]) {
            return section
        }
    }
    return "overview"
}

/**
 * Prefer completedSteps when any flag is set; otherwise fall back to legacy interview step.
 */
export const resolveV2ResumeSection = ({ completedSteps, legacyInterviewStep } = {}) => {
    const steps = completedSteps || {}
    const hasAnyCompleted = Object.values(steps).some(Boolean)
    if (hasAnyCompleted) {
        return firstIncompleteV2Section(steps)
    }
    if (legacyInterviewStep != null && legacyInterviewStep !== "") {
        return mapLegacyInterviewStepToV2Section(legacyInterviewStep)
    }
    return "yourinfo"
}

/**
 * Multi-coborrower continue: walk participantOrder and resume the first party
 * who still has an incomplete interview section. Only go to overview when
 * every participant has finished APPLICATION_V2_FLOW_SECTIONS.
 */
export const resolveV2ResumeTarget = ({
    application,
    legacyInterviewStep,
} = {}) => {
    const order = Array.isArray(application?.participantOrder) ? application.participantOrder : []
    const byId = application?.participantsById || {}
    const primaryPartyId = application?.primaryPartyId || application?.activePartyId || order[0] || ""

    let anyCompletedAcrossParties = false
    for (const partyId of order) {
        const steps = byId[partyId]?.completedSteps || {}
        if (Object.values(steps).some(Boolean)) {
            anyCompletedAcrossParties = true
            break
        }
    }

    if (!anyCompletedAcrossParties && legacyInterviewStep != null && legacyInterviewStep !== "") {
        return {
            partyId: primaryPartyId,
            section: mapLegacyInterviewStepToV2Section(legacyInterviewStep),
        }
    }

    for (const partyId of order) {
        const steps = byId[partyId]?.completedSteps || {}
        const section = firstIncompleteV2Section(steps)
        if (section !== "overview") {
            return { partyId, section }
        }
    }

    return { partyId: primaryPartyId, section: "overview" }
}

/**
 * Legacy SSN.js: Authorize & submit only when the loan can actually be turned in.
 * - currentStatus is ApplicationStarted (empty V2 status counts as started)
 * - current party has every flow step except creditcheck
 * - every other party has every flow step including creditcheck
 */
export const canAuthorizeAndSubmitV2 = (application, partyId) => {
    const status = application?.currentStatus || "ApplicationStarted"
    if (status !== "ApplicationStarted") {
        return false
    }

    const order = Array.isArray(application?.participantOrder) ? application.participantOrder : []
    const byId = application?.participantsById || {}
    if (!partyId) {
        return order.length === 0
    }

    const currentSteps = byId[partyId]?.completedSteps || {}
    const currentReady = APPLICATION_V2_FLOW_SECTIONS
        .filter((section) => section !== "creditcheck")
        .every((section) => Boolean(currentSteps[section]))
    if (!currentReady) {
        return false
    }
    if (order.length === 0) {
        return true
    }

    return order.every((id) => {
        if (id === partyId) {
            return true
        }
        const steps = byId[id]?.completedSteps || {}
        return APPLICATION_V2_FLOW_SECTIONS.every((section) => Boolean(steps[section]))
    })
}

export const buildApplicationV2Path = (section, partyId) => {
    const safeSection = section || "yourinfo"
    if (partyId) {
        return `/application/v2/interview/${safeSection}/${partyId}`
    }
    return `/application/v2/interview/${safeSection}`
}

export const readLegacyInterviewStepFromSession = () => {
    try {
        const raw = sessionStorage.getItem("state")
        if (!raw) {
            return null
        }
        const st = JSON.parse(raw)
        return st?.progress?.interview?.step ?? null
    } catch (err) {
        return null
    }
}
